import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from 'antd';

import {
	createQuickAddTemplate,
	deleteQuickAddTemplate,
	demoteQuickAddTemplate,
	getQuickAddTemplateLists,
	promoteQuickAddTemplate,
	updateQuickAddTemplate,
} from '../services/quickAddTemplates';
import { normalizeId } from '../utils/ids';
import { publishQuickAddTemplates } from './useDuplicateProfiles';

/**
 * The collection's quick add templates, in the useSavedFilters idiom: every
 * mutation applies optimistically and rolls the whole list back if the
 * server refuses. No provider — templates are scoped to one collection and
 * fetched by the surface that shows them.
 *
 * `enabled` lets a host defer the fetch until it is actually needed: the Add
 * item modal asks for the list only once it opens, so a trigger sitting in
 * the context bar costs nothing until clicked.
 *
 * Mutations a user can plausibly get WRONG (a duplicate name → 409) still
 * reject after rolling back, so the calling modal can show the message inline
 * instead of a toast the user has already dismissed by the time they retype.
 */

/** The two tiers a template can live in — the same split as favorites. */
export const QUICK_ADD_SCOPE = { COLLECTION: 'collection', USER: 'user' };

const tierRank = scope => (scope === QUICK_ADD_SCOPE.COLLECTION ? 0 : 1);

/** Collection tier first, name order within each — the server's own ordering. */
export const sortQuickAddTemplates = list => [...list].sort((a, b) => (
	tierRank(a?.scope) - tierRank(b?.scope)
	|| (a?.name || '').localeCompare(b?.name || '')
));

const replaceById = (list, id, next) => sortQuickAddTemplates(
	list.map(entry => (normalizeId(entry?._id) === id ? { ...entry, ...next } : entry))
);

export default function useQuickAddTemplates(collectionId, { enabled = true } = {}) {
	const { message } = App.useApp();
	// The collection's own templates only: a sub-collection inherits none
	// from the collections above it (owner ruling 2026-10-21, KPR-1084).
	const [templates, setTemplates] = useState([]);
	const [loading, setLoading] = useState(false);
	// The committed list, for the rollback snapshot: a state updater may run
	// lazily (during the next render) rather than at the call, so a snapshot
	// taken only inside one is not guaranteed to exist by the time the
	// mutation fails.
	const templatesRef = useRef(templates);
	templatesRef.current = templates;
	// Bumped by reload(); the fetch effect keys on it.
	const [fetchKey, setFetchKey] = useState(0);
	// Set once a fetch has landed for this collection: only then is the list
	// an answer worth sharing with the Duplicate menus (useDuplicateProfiles)
	// — the empty list before it is not.
	const [loadedFor, setLoadedFor] = useState(null);
	// A first read has answered for this collection, whatever the answer was,
	// and whether it failed: until then `loading` is true, even on the render
	// before the effect runs, so no list draws "No quick adds yet" ahead of it.
	const [settledFor, setSettledFor] = useState(null);
	const [loadError, setLoadError] = useState(false);

	useEffect(() => {
		if (!collectionId || !enabled) return undefined;
		let active = true;
		setLoading(true);
		setLoadError(false);
		getQuickAddTemplateLists(collectionId)
			.then(({ templates: list }) => {
				if (!active) return;
				setTemplates(sortQuickAddTemplates(list));
				setLoadedFor(collectionId);
			})
			// Quick adds are an enhancement over a modal that works without
			// them; a failed load leaves the Add item dialog empty and quiet,
			// and says so to the list that asks (`loadError`), which offers Retry.
			.catch(() => { if (active) { setTemplates([]); setLoadError(true); } })
			.finally(() => {
				if (!active) return;
				setLoading(false);
				setSettledFor(collectionId);
			});
		return () => { active = false; };
	}, [collectionId, enabled, fetchKey]);

	// A collection switch drops the previous collection's rows at once rather
	// than showing them until the next fetch lands.
	useEffect(() => { setTemplates([]); setLoadedFor(null); setLoadError(false); }, [collectionId]);

	// Every list this store holds — fetched, or changed by an edit, a pin, a
	// new default for Duplicate — is the freshest the client has: the item
	// surfaces' Duplicate ▾ follows it at once.
	useEffect(() => {
		if (collectionId && loadedFor === collectionId) publishQuickAddTemplates(collectionId, templates);
	}, [collectionId, loadedFor, templates]);

	const reload = useCallback(() => setFetchKey(key => key + 1), []);

	/**
	 * Applies `optimistic` immediately, runs `mutation`, and restores the
	 * previous list if it fails. `rethrow` lets a caller (the template modal)
	 * show the server's own message where the user is still typing.
	 */
	const run = useCallback(async (optimistic, mutation, { errorText, rethrow = false } = {}) => {
		let snapshot = templatesRef.current;
		setTemplates(current => { snapshot = current; return optimistic(current); });
		try {
			const result = await mutation();
			if (result) {
				const id = normalizeId(result._id);
				setTemplates(current => {
					const known = current.some(entry => normalizeId(entry?._id) === id);
					return known ? replaceById(current, id, result) : sortQuickAddTemplates([...current, result]);
				});
			}
			return result;
		} catch (error) {
			setTemplates(snapshot);
			if (rethrow) throw error;
			message.error(error?.response?.data?.message || errorText);
			return null;
		}
	}, [message]);

	// The temporary row a POST shows before the server answers. Its id is a
	// placeholder: the real row replaces it wholesale on success.
	const create = useCallback((draft) => {
		const pending = { ...draft, _id: `pending-${Date.now()}`, scope: QUICK_ADD_SCOPE.USER, pending: true };
		return run(
			current => sortQuickAddTemplates([...current, pending]),
			() => createQuickAddTemplate(collectionId, draft)
				// Drop the placeholder before the real row lands, so the list
				// never briefly shows the template twice.
				.then(saved => { setTemplates(cur => cur.filter(e => e !== pending)); return saved; })
				.catch(error => { setTemplates(cur => cur.filter(e => e !== pending)); throw error; }),
			{ errorText: 'Could not save the quick add.', rethrow: true }
		);
	}, [collectionId, run]);

	const update = useCallback((template, patch) => {
		const id = normalizeId(template?._id);
		// One default for Duplicate per card: the API clears the others, and
		// so does the optimistic list (the server answers for this row only).
		const cardKey = normalizeId(template?.card_id);
		const clearsOthers = patch?.defaultForDuplicate === true;
		return run(
			current => {
				const next = replaceById(current, id, patch);
				return clearsOthers
					? next.map(entry => (normalizeId(entry?._id) !== id && normalizeId(entry?.card_id) === cardKey && entry?.defaultForDuplicate
						? { ...entry, defaultForDuplicate: false }
						: entry))
					: next;
			},
			() => updateQuickAddTemplate(collectionId, id, patch),
			{ errorText: 'Could not save the quick add.', rethrow: true }
		);
	}, [collectionId, run]);

	const remove = useCallback((template) => {
		const id = normalizeId(template?._id);
		return run(
			current => current.filter(entry => normalizeId(entry?._id) !== id),
			() => deleteQuickAddTemplate(collectionId, id).then(() => null),
			{ errorText: 'Could not delete the quick add.' }
		);
	}, [collectionId, run]);

	const promote = useCallback((template) => {
		const id = normalizeId(template?._id);
		return run(
			current => replaceById(current, id, { scope: QUICK_ADD_SCOPE.COLLECTION }),
			() => promoteQuickAddTemplate(collectionId, id),
			{ errorText: 'Could not pin the quick add for everyone.' }
		);
	}, [collectionId, run]);

	const demote = useCallback((template) => {
		const id = normalizeId(template?._id);
		return run(
			// A personal template is nobody's default for Duplicate (the API drops it too).
			current => replaceById(current, id, { scope: QUICK_ADD_SCOPE.USER, defaultForDuplicate: false }),
			() => demoteQuickAddTemplate(collectionId, id),
			{ errorText: 'Could not unpin the quick add.' }
		);
	}, [collectionId, run]);

	const waiting = loading || Boolean(collectionId && enabled && settledFor !== collectionId);

	return { templates, loading: waiting, loadError, reload, create, update, remove, promote, demote };
}
